import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Calendar, Users, Stethoscope, Package, FileText, BookOpen, Brain, Eye, Calculator,
  StickyNote, MessageSquare, Bell, Settings, Sun, Moon, ScrollText, ClipboardList, Folder, Star,
  BarChart3, Scale, GraduationCap, Layers, Compass, HelpCircle, ShieldCheck,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

// Chaque commande peut déclarer :
//   - `rubrique`  : clé unique d'autorisation (rubrique ou sous-rubrique)
//   - `anyOf`     : tableau de clés — accessible si l'une est autorisée
//   - `always`    : true → toujours dispo (préférences globales)
//   - `directorOnly`: true → réservé Directeur
export const ALL_COMMANDS = [
  // Navigation principale
  { id: "nav-actu", label: "Actualités", group: "Navigation", icon: Bell, action: "tab:actualites", rubrique: "actualites" },
  { id: "nav-equipe", label: "Équipe", group: "Navigation", icon: Users, action: "tab:equipe", rubrique: "personnel" },
  { id: "nav-planning", label: "Planning", group: "Navigation", icon: Calendar, action: "tab:planning", rubrique: "planning" },
  { id: "nav-conges", label: "Congés", group: "Navigation", icon: Calendar, action: "tab:conges", rubrique: "conges" },
  { id: "nav-messages", label: "Messages", group: "Navigation", icon: MessageSquare, action: "tab:messages", rubrique: "messages" },
  { id: "nav-bloc", label: "Bloc", group: "Navigation", icon: Stethoscope, action: "tab:bloc", anyOf: ["ressources","bloc_operatoire","stocks","stocks_global","materiel_bloc"] },
  { id: "nav-ressources", label: "Ressources", group: "Navigation", icon: Folder, action: "tab:ressources", anyOf: ["ressources","ressources_docs","tutos","infos_patient","transcription","champ_visuel","stocks","stocks_global","materiel_inventaire","notebook","notes","aide_decision","projets","curriculum_vitae","recap_mensuel","primes"] },
  { id: "nav-comptab", label: "Comptabilité", group: "Navigation", icon: Scale, action: "tab:comptabilite", anyOf: ["comptabilite","registre_factures","coffre_fort_factures","recuperation_especes","compta_dashboard","caisse_factures","analyses_annuelles","frais_cabinet","bons_livraison","tiers_payant"] },

  // Comptabilité — sous-onglets
  { id: "compta-analytics", label: "Tableau de bord analytique", group: "Comptabilité", icon: BarChart3, action: "subtab:comptabilite:analytics", keywords: "analytics dashboard kpi statistique tableau bord", anyOf: ["comptabilite","compta_dashboard"] },
  { id: "compta-analyse", label: "Analyse caisse", group: "Comptabilité", icon: BarChart3, action: "subtab:comptabilite:analyse", anyOf: ["comptabilite","caisse_factures"] },
  { id: "compta-coherence", label: "Cohérence caisse/planning", group: "Comptabilité", icon: Scale, action: "subtab:comptabilite:coherence", anyOf: ["comptabilite","caisse_factures"] },
  { id: "compta-registre", label: "Registre factures", group: "Comptabilité", icon: ScrollText, action: "subtab:comptabilite:registre", anyOf: ["comptabilite","registre_factures"] },
  { id: "compta-generateur", label: "Générateur factures", group: "Comptabilité", icon: FileText, action: "subtab:comptabilite:generateur", anyOf: ["comptabilite","caisse_factures"] },
  { id: "compta-frais", label: "Frais cabinet", group: "Comptabilité", icon: Folder, action: "subtab:comptabilite:frais", anyOf: ["comptabilite","frais_cabinet"] },

  // Bloc — sous-onglets
  { id: "bloc-iol", label: "Calculateur IOL", group: "Bloc", icon: Calculator, action: "subtab:bloc:iol-calculator", keywords: "implant cataracte biométrie pearl srk haigis", rubrique: "bloc_operatoire" },
  { id: "bloc-prog", label: "Programmation", group: "Bloc", icon: ClipboardList, action: "subtab:bloc:programmation", keywords: "dossiers analyse pack opération planification patient", rubrique: "bloc_operatoire" },
  { id: "bloc-op", label: "Bloc opératoire", group: "Bloc", icon: Stethoscope, action: "subtab:bloc:bloc-op", rubrique: "bloc_operatoire" },
  { id: "bloc-mat", label: "Matériel Bloc", group: "Bloc", icon: Package, action: "subtab:bloc:materiel", rubrique: "materiel_bloc" },
  { id: "ress-stocks", label: "Gestion Stocks", group: "Ressources", icon: Package, action: "subtab:ressources:stocks", rubrique: "stocks" },
  { id: "ress-stock-global", label: "Réserves des stocks", group: "Ressources", icon: Package, action: "subtab:ressources:stock-global", rubrique: "stocks_global" },
  { id: "bloc-pat", label: "Patients à adresser", group: "Bloc", icon: Users, action: "subtab:bloc:patients-adresser", rubrique: "bloc_operatoire" },
  { id: "bloc-dem", label: "Demandes d'examen", group: "Bloc", icon: ClipboardList, action: "subtab:bloc:demandes-examen", rubrique: "bloc_operatoire" },

  // Ressources — sous-onglets
  { id: "res-tutos", label: "Docs & Tutos", group: "Ressources", icon: FileText, action: "subtab:ressources:tutos", rubrique: "tutos" },
  { id: "res-notes", label: "Notes", group: "Ressources", icon: StickyNote, action: "subtab:ressources:notes", rubrique: "notes" },
  { id: "res-aide", label: "Aide à la décision", group: "Ressources", icon: Brain, action: "subtab:ressources:aide-decision", keywords: "examen protocole diagnostic", rubrique: "aide_decision" },
  { id: "res-strab", label: "Simulateur strabisme", group: "Ressources", icon: Eye, action: "subtab:ressources:strabisme", keywords: "strabisme cover test phorie tropie", rubrique: "aide_decision" },
  { id: "res-ocr", label: "OCR Ordonnance", group: "Ressources", icon: ScrollText, action: "subtab:ressources:ocr-ordonnance", keywords: "ocr scan ordonnance prescription tesseract photo", rubrique: "aide_decision" },
  { id: "res-notebook", label: "Bibliothèque (NotebookLM)", group: "Ressources", icon: BookOpen, action: "subtab:ressources:notebook", keywords: "recherche document pdf", rubrique: "notebook" },
  { id: "res-projets", label: "Projets", group: "Ressources", icon: Folder, action: "subtab:ressources:projets", rubrique: "projets" },
  { id: "res-cv", label: "Curriculum Vitae", group: "Ressources", icon: FileText, action: "subtab:ressources:curriculum", rubrique: "curriculum_vitae" },
  { id: "res-recap", label: "Récap mensuel", group: "Ressources", icon: BarChart3, action: "subtab:ressources:recap-mensuel", rubrique: "recap_mensuel" },
  { id: "res-primes", label: "Primes", group: "Ressources", icon: Star, action: "subtab:ressources:primes", rubrique: "primes" },

  // Outils
  { id: "tool-quizz", label: "Quizz", group: "Outils", icon: GraduationCap, action: "subtab:actualites:quizz", rubrique: "quizz" },
  { id: "tool-plan-cab", label: "Plan du cabinet", group: "Outils", icon: Layers, action: "subtab:actualites:plan-cabinet", rubrique: "plan_cabinet" },

  // Préférences (toujours accessibles)
  { id: "diag-planning", label: "Diagnostic Planning (assignations incohérentes)", group: "Outils admin", icon: ShieldCheck, action: "open-planning-diagnostic", keywords: "diagnostic planning bug doublon centre incohérent julie cleanup nettoyer", directorOnly: true },
  { id: "pref-logout", label: "Se déconnecter", group: "Préférences", icon: Settings, action: "logout", always: true },
];

// Score de pertinence simple (substring + start-match boost)
const score = (label, kw, query) => {
  const t = (label + " " + (kw || "")).toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  if (t.startsWith(q)) return 100;
  if (t.includes(q)) return 50;
  // match par mots
  const words = q.split(/\s+/);
  let s = 0;
  for (const w of words) if (t.includes(w)) s += 10;
  return s;
};

const CommandPalette = ({ open, onClose, onAction }) => {
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    if (!query.trim()) return ALL_COMMANDS;
    return ALL_COMMANDS
      .map(c => ({ ...c, _score: score(c.label, c.keywords, query) }))
      .filter(c => c._score > 0)
      .sort((a, b) => b._score - a._score);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => { setSelectedIdx(0); }, [query]);

  const runAction = useCallback((cmd) => {
    if (!cmd) return;
    if (cmd.action === "toggle-theme") {
      toggleTheme();
      onClose();
      return;
    }
    if (cmd.action === "logout") {
      onAction?.({ type: "logout" });
      onClose();
      return;
    }
    if (cmd.action === "open-planning-diagnostic") {
      onAction?.({ type: "open-planning-diagnostic" });
      onClose();
      return;
    }
    if (cmd.action.startsWith("tab:")) {
      const tab = cmd.action.split(":")[1];
      onAction?.({ type: "tab", tab });
      onClose();
      return;
    }
    if (cmd.action.startsWith("subtab:")) {
      const [, parentTab, subTab] = cmd.action.split(":");
      onAction?.({ type: "subtab", parentTab, subTab });
      onClose();
      return;
    }
    onClose();
  }, [onClose, onAction, toggleTheme]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runAction(filtered[selectedIdx]);
    }
  };

  // Scroll item into view on selection change
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${selectedIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selectedIdx, filtered.length]);

  if (!open) return null;

  // Group by group
  const grouped = filtered.reduce((acc, c) => {
    (acc[c.group] = acc[c.group] || []).push(c);
    return acc;
  }, {});

  let runningIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh] px-3 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-900 dark:text-slate-100 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-slate-700">
          <Search className="h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Tapez pour chercher une page, un module, une action…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="flex-1 outline-none bg-transparent text-sm text-gray-800 dark:text-slate-100 placeholder:text-gray-400"
            data-testid="cmdk-input"
          />
          <kbd className="text-[10px] bg-gray-100 dark:bg-slate-800 dark:text-slate-300 text-gray-500 px-1.5 py-0.5 rounded font-mono">ESC</kbd>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-slate-500">Aucun résultat</div>
          )}
          {Object.entries(grouped).map(([group, cmds]) => (
            <div key={group}>
              <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">{group}</div>
              {cmds.map(cmd => {
                runningIdx += 1;
                const isSel = runningIdx === selectedIdx;
                const Icon = cmd.id === "pref-theme" ? (theme === "dark" ? Sun : Moon) : cmd.icon;
                const idx = runningIdx;
                return (
                  <button
                    key={cmd.id}
                    data-cmd-idx={idx}
                    onClick={() => runAction(cmd)}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                      isSel
                        ? "bg-[#1173D4]/10 text-[#1173D4] dark:bg-blue-500/15 dark:text-blue-300"
                        : "text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800"
                    }`}
                    data-testid={`cmdk-item-${cmd.id}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 text-left">{cmd.label}</span>
                    {isSel && <kbd className="text-[10px] bg-gray-100 dark:bg-slate-800 px-1 py-0.5 rounded">↵</kbd>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 px-3 py-1.5 text-[10px] text-gray-400 dark:text-slate-500 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950">
          <span><kbd className="bg-white dark:bg-slate-800 px-1 py-0.5 rounded border">↑↓</kbd> Naviguer</span>
          <span><kbd className="bg-white dark:bg-slate-800 px-1 py-0.5 rounded border">↵</kbd> Sélectionner</span>
          <span><kbd className="bg-white dark:bg-slate-800 px-1 py-0.5 rounded border">esc</kbd> Fermer</span>
          <span className="ml-auto"><kbd className="bg-white dark:bg-slate-800 px-1 py-0.5 rounded border">⌘K</kbd></span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
